<template>
  <div
    id="app"
    class="h-100"
    :class="[skinClasses]"
  >
    <component :is="layout">
      <router-view />
    </component>

    <scroll-to-top v-if="enableScrollToTop" />
    <TwoFactorInvitation v-if="showSecurityUI" />
  </div>
</template>

<script>
import ScrollToTop from '@core/components/scroll-to-top/ScrollToTop.vue'

import { useAppStore } from '@/stores/appStore.js';
import { useAppConfigStore } from '@/stores/appConfigStore.js';

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
//import { provideToast } from 'vue-toastification/composition'
import { computed, defineAsyncComponent, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ability from '@/libs/acl/ability'
import { initialAbility } from '@/libs/acl/config'
import { useTwoFactorStore } from '@/stores/twoFactorStore'
import TwoFactorInvitation from '@/views/pages/account-setting/TwoFactorInvitation.vue'
import axios from '@axios'
import { readStoredAccountProfile, captureProfileRequest, applyProfileResponse } from '@/auth/accountProfile'
import { clearProfilePhotos } from '@/auth/profilePhotos'

import useAppConfig from '@core/app-config/useAppConfig'

import { useWindowSize, useCssVar } from '@vueuse/core'

const LayoutVertical = defineAsyncComponent(() => import('@/layouts/vertical/LayoutVertical.vue'))
const LayoutHorizontal = defineAsyncComponent(() => import('@/layouts/horizontal/LayoutHorizontal.vue'))
const LayoutFull = defineAsyncComponent(() => import('@/layouts/full/LayoutFull.vue'))

export default {
  components: {

    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,

    ScrollToTop,
    TwoFactorInvitation,
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.appConfigStore.layout.type
    },
  },
  beforeCreate() {
    // Directional theme rules must apply even when CSS variables are unavailable.
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')

    //  Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement, { initialValue: '' }).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement, { initialValue: '' }).value.slice(0, -2))
    }
  },
  setup() {

    const { skin, skinClasses } = useAppConfig()
    const { enableScrollToTop } = $themeConfig.layout
    const appStore = useAppStore()
    const appConfigStore = useAppConfigStore()
    const twoFactorStore = useTwoFactorStore()
    const route = useRoute()
    const router = useRouter()
    const leaveAccountChannel = accountId => {
      if (accountId == null) return
      try { window.Echo?.leave?.('App.Models.People.' + accountId) } catch { /* Session cleanup must still finish. */ }
    }
    const isSecurityPage = () => route.name === 'pages-account-setting' && route.query.tab === 'security'
    const showSecurityUI = computed(() => JSON.parse(appConfigStore.layout.userdata || 'null')?.demo_mode !== true
      && route.meta.layout !== 'full'
      && Boolean(twoFactorStore.accountId) && !isSecurityPage())
    const syncSecurityStatus = () => {
      twoFactorStore.syncAccount()
      if (isSecurityPage()) twoFactorStore.closeInvitation()
      if (route.meta.layout !== 'full' && twoFactorStore.accountId) twoFactorStore.fetchStatus()
    }
    const refreshAccountProfile = async () => {
      const snapshot = captureProfileRequest('profile-refresh')
      if (!snapshot.id || !snapshot.token) return
      try {
        const response = await axios.get('/api/people/')
        const profile = applyProfileResponse(snapshot, response, false)
        if (!profile) return
        appConfigStore.updateUserdata(JSON.stringify(profile))
        ability.update(profile.ability || initialAbility)
      } catch { /* Keep the safe local profile during a temporary outage. */ }
    }
    watch(() => appConfigStore.layout.userdata, syncSecurityStatus)
    watch(() => route.meta.layout === 'full', (isFull, wasFull) => {
      if (isFull && !wasFull) twoFactorStore.closeInvitation()
      syncSecurityStatus()
    })
    watch(() => [route.name, route.query.tab], () => {
      if (isSecurityPage()) twoFactorStore.closeInvitation()
    })
    const handleStorage = event => {
      if (event.key !== 'userData' && event.key !== 'accessToken' && event.key !== null) return
      const previousId = JSON.parse(appConfigStore.layout.userdata || 'null')?.id
      const profile = readStoredAccountProfile()
      if (!localStorage.getItem('accessToken') || previousId !== profile?.id) leaveAccountChannel(previousId)
      appConfigStore.layout.userdata = JSON.stringify(profile)
      ability.update(profile?.ability || initialAbility)
      syncSecurityStatus()
      // Same-account profile writes are already safe; fetching again would echo
      // activity timestamps between tabs indefinitely.
      if (event.key !== 'userData' || previousId !== profile?.id) refreshAccountProfile()
    }
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') syncSecurityStatus()
    }
    const handleExpiredAuthentication = () => {
      const accountId = twoFactorStore.accountId || JSON.parse(appConfigStore.layout.userdata || 'null')?.id
      leaveAccountChannel(accountId)
      clearProfilePhotos()
      appConfigStore.layout.userdata = null
      twoFactorStore.reset()
      ability.update(initialAbility)
      router.replace({ name: 'auth-login' }).catch(() => {})
    }
    onMounted(() => {
      syncSecurityStatus()
      refreshAccountProfile()
      window.addEventListener('focus', syncSecurityStatus)
      window.addEventListener('storage', handleStorage)
      document.addEventListener('visibilitychange', handleVisibility)
      window.addEventListener('yournutri:auth-session-expired', handleExpiredAuthentication)
    })
    onUnmounted(() => {
      clearProfilePhotos()
      window.removeEventListener('focus', syncSecurityStatus)
      window.removeEventListener('storage', handleStorage)
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('yournutri:auth-session-expired', handleExpiredAuthentication)
    })

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    appStore.updateWindowWidth(window.innerWidth)

    const { width: windowWidth, height: windowHeight } = useWindowSize()
    const updateViewportHeight = val => {
      document.documentElement.style.setProperty('--vh', `${val * 0.01}px`)
    }

    updateViewportHeight(window.innerHeight)
    watch(windowWidth, val => {
      appStore.updateWindowWidth(val)
    })
    watch(windowHeight, val => {
      updateViewportHeight(val)
    })

    return {
      appConfigStore,
      showSecurityUI,
      skinClasses,
      enableScrollToTop,
    }
  },
}
</script>
